/**
 * ParagraphChat – multi-turn AI-chat om en enskild lagparagraf.
 * Renderas inbäddad i LawBrowser under en paragraf. Håller en isolerad session
 * per (sfs, chapter, paragraph) i minnet så användaren kan ställa följdfrågor.
 */
import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, Send, Loader2, User, Sparkles, X, RotateCcw, AlertTriangle, Share2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import FeedbackButtons from "@/components/law/FeedbackButtons";
import ShareChatDialog from "@/components/law/ShareChatDialog";

const MAX_MESSAGE_CHARS = 500;

export default function ParagraphChat({ sfs, chapter, paragraph, onClose }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null); // {kind, text}
  const [shareOpen, setShareOpen] = useState(false);
  const [paragraphText, setParagraphText] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofokus när panelen öppnas
    inputRef.current?.focus();
    // Hämta paragraftexten så PDF-export har den (skickas till ShareChatDialog)
    (async () => {
      try {
        const { data } = await api.get(`/laws/${sfs}/${chapter}/${paragraph}`);
        setParagraphText(data.text || null);
      } catch {
        // Ignorera – paragrafen är valfri för PDF
      }
    })();
  }, [sfs, chapter, paragraph]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    if (msg.length > MAX_MESSAGE_CHARS) {
      toast.error(`Frågan är för lång (max ${MAX_MESSAGE_CHARS} tecken).`);
      return;
    }

    // Optimistisk rendering
    const nowIso = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", text: msg, ts: nowIso }]);
    const previousInput = msg;
    setInput("");
    setErrorBanner(null);
    setLoading(true);

    try {
      const { data } = await api.post("/laws/chat", {
        sfs,
        chapter,
        paragraph,
        message: msg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages(data.messages);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailText = typeof detail === "string" ? detail : null;
      // Ta bort optimistiskt meddelande vid fel, men återställ användarens text så de kan retrya
      setMessages((prev) => prev.slice(0, -1));
      setInput(previousInput);
      if (status === 402) {
        setErrorBanner({ kind: "budget", text: "AI-budgeten är slut just nu. Kommer tillbaka inom kort – prova gärna 'AI-förklara' som är cachad." });
      } else if (status === 503) {
        setErrorBanner({ kind: "service", text: detailText || "AI-tjänsten är tillfälligt otillgänglig. Försök igen om en stund." });
      } else if (status === 429) {
        toast.error(detailText || "För många AI-anrop – vänta lite.");
      } else if (status === 413) {
        toast.error("Frågan är för lång.");
      } else {
        toast.error(detailText || "AI-chatten misslyckades");
      }
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    setErrorBanner(null);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = [
    "Vad betyder detta i praktiken?",
    "Vilka undantag finns?",
    "Vad händer om jag inte följer regeln?",
    "Behöver jag söka bygglov?",
  ];

  return (
    <div
      className="mt-3 border-l-4 border-blue-600 bg-blue-50/40 rounded-r-sm overflow-hidden"
      data-testid={`law-chat-${paragraph}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-blue-100/50 border-b border-blue-200">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-900 uppercase tracking-[0.15em]">
          <MessageSquare className="w-3.5 h-3.5" />
          Chatta om {chapter}:{paragraph} §
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <>
              <button
                onClick={() => setShareOpen(true)}
                data-testid={`law-chat-share-${paragraph}`}
                className="p-1.5 hover:bg-blue-200/60 rounded-sm text-blue-700"
                aria-label="Dela chatt"
                title="Dela / ladda ner"
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={reset}
                data-testid={`law-chat-reset-${paragraph}`}
                className="p-1.5 hover:bg-blue-200/60 rounded-sm text-blue-700"
                aria-label="Rensa chatt"
                title="Starta om"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <button
            onClick={onClose}
            data-testid={`law-chat-close-${paragraph}`}
            className="p-1.5 hover:bg-blue-200/60 rounded-sm text-blue-700"
            aria-label="Stäng chatt"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {shareOpen && sessionId && (
        <ShareChatDialog
          sessionId={sessionId}
          sfs={sfs}
          chapter={chapter}
          paragraph={paragraph}
          paragraphText={paragraphText}
          messages={messages}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Meddelanden */}
      <div
        ref={scrollRef}
        className="max-h-80 overflow-y-auto px-4 py-3 space-y-3 bg-white"
      >
        {errorBanner && (
          <div
            data-testid={`law-chat-error-${paragraph}`}
            className="flex items-start gap-2 border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 text-xs text-amber-900"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold mb-0.5">
                {errorBanner.kind === "budget" ? "AI-budget slut" : "Tjänsten är otillgänglig"}
              </div>
              <div className="leading-relaxed">{errorBanner.text}</div>
            </div>
            <button
              onClick={() => setErrorBanner(null)}
              className="text-amber-700 hover:text-amber-900 p-0.5"
              aria-label="Stäng meddelande"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {messages.length === 0 && (
          <div className="text-center py-4">
            <MessageSquare className="w-6 h-6 mx-auto text-blue-400 mb-2 opacity-60" />
            <p className="text-xs text-zinc-600 mb-3">
              Ställ en fråga om denna paragraf. AI:n svarar utifrån lagtexten.
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  data-testid={`law-chat-suggestion-${paragraph}`}
                  className="text-[11px] px-2.5 py-1 border border-blue-200 rounded-full text-blue-700 hover:bg-blue-50 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div
              className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                m.role === "user" ? "bg-zinc-800 text-white" : "bg-blue-600 text-white"
              }`}
            >
              {m.role === "user" ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            </div>
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "flex flex-col"}`}>
              <div
                className={`rounded-sm px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-zinc-800 text-white"
                    : "bg-blue-50 border border-blue-100 text-zinc-800"
                }`}
                data-testid={`law-chat-msg-${paragraph}-${i}`}
              >
                {m.text}
              </div>
              {m.role === "assistant" && sessionId && (
                <FeedbackButtons
                  kind="law_chat"
                  sessionId={sessionId}
                  messageIndex={i}
                  sfs={sfs}
                  chapter={chapter}
                  paragraph={paragraph}
                />
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-2 items-center">
            <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center">
              <Sparkles className="w-3 h-3" />
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-sm px-3 py-2 text-sm text-zinc-500 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Tänker…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-blue-200 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            data-testid={`law-chat-input-${paragraph}`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Skriv din fråga… (Enter för att skicka)"
            rows={1}
            maxLength={MAX_MESSAGE_CHARS}
            className="flex-1 resize-none px-3 py-2 border border-zinc-300 rounded-sm text-sm bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none max-h-24"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            data-testid={`law-chat-send-${paragraph}`}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-sm inline-flex items-center gap-1.5 text-sm font-medium transition"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
        <div className="flex items-center justify-between mt-1.5 px-0.5">
          <span className="text-[10px] text-zinc-500">
            AI kan ha fel – bekräfta viktiga beslut med kommunen.
          </span>
          <span className={`text-[10px] tabular-nums ${input.length > MAX_MESSAGE_CHARS * 0.9 ? "text-orange-600" : "text-zinc-400"}`}>
            {input.length}/{MAX_MESSAGE_CHARS}
          </span>
        </div>
      </div>
    </div>
  );
}
